import { useIntervalFn } from '@vueuse/core';
import { CanceledError } from 'axios';
import { defineStore } from 'pinia';

import type {
  Broker,
  Rights,
  SettingsStoreData,
} from '@/features/Broker/types';
import { useTariffStore } from '@/features/Tariffs/store';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import type { ResourceContainer } from '@/types';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';
import { SessionError } from '@/services/errorHandler/SessionError';
import { settingsSchema } from '@/features/Broker/settingsSchema';

interface BrokerStore {
  rights: ResourceContainer<undefined | Rights>;
  broker: ResourceContainer<undefined | Broker>;
  calledSoftfairMaklerApi: boolean;
  isSoftfairTransmitted: boolean;
  isSoftfairBrokerIdTransmitted: boolean;
  settings: SettingsStoreData;
  bedarfInformation: {
    clientId: string | undefined;
    bedarfId: string | undefined;
  };
  heartbeatStarted: boolean;
}

export const useBrokerStore = defineStore('BrokerStore', {
  state: (): BrokerStore => ({
    rights: {
      isLoading: false,
      data: undefined,
    },
    broker: {
      isLoading: false,
      data: undefined,
    },
    settings: {
      isLoading: false,
      data: settingsSchema.getDefault(),
    },

    calledSoftfairMaklerApi: false,
    isSoftfairTransmitted: true,
    isSoftfairBrokerIdTransmitted: false,

    bedarfInformation: {
      bedarfId: undefined,
      clientId: undefined,
    },

    heartbeatStarted: false,
  }),

  actions: {
    async loadBroker() {
      try {
        const api = useApi().broker;

        this.broker.isLoading = true;
        const [, request] = api.loadBroker();

        const result = await request;
        if (result === null) {
          throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
            additionalErrorMessage: 'broker -> loadBroker',
          });
        }

        this.broker.data = result;

        // load rights for this broker
        await this.loadRights().catch(() => {
          throw new VglError(VGL_ERROR_CODE.BROKER_LOADING_RIGHTS_FAILED);
        });
        // start heartbeat for user session after
        // successfully loading the user
        this.startHeartbeat();
        // try loading user settings
        this.loadSettings().catch(() => {
          throw new VglError(VGL_ERROR_CODE.BROKER_LOADING_SETTINGS_FAILED);
        });
      } catch (error) {
        if (
          !(error instanceof CanceledError) &&
          !(error instanceof SessionError)
        ) {
          throw new VglError(VGL_ERROR_CODE.BROKER_LOADING_FAILED, {
            additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
            cause: error,
          });
        }

        throw error;
      } finally {
        this.broker.isLoading = false;
      }
    },

    async transmitSoftfairBrokerData() {
      if (this.isSoftfairTransmitted && this.calledSoftfairMaklerApi) {
        return;
      }

      this.isSoftfairTransmitted = false;
      const api = useApi().broker;
      const [, agentRequest] = api.transmitSoftfairBrokerData();

      await agentRequest;

      this.isSoftfairTransmitted = true;
      this.calledSoftfairMaklerApi = true;
    },

    async transmitSoftfairBrokerId(
      brokerId: string,
      insuranceId: string,
      calculationId: string,
      pool?: string,
    ) {
      this.isSoftfairBrokerIdTransmitted = false;
      const api = useApi().broker;
      const [, agentRequest] = api.transmitSoftfairBrokerId(
        brokerId,
        insuranceId,
        calculationId,
        pool,
      );

      try {
        await agentRequest;
        this.isSoftfairBrokerIdTransmitted = true;
      } catch (error) {
        this.isSoftfairBrokerIdTransmitted = false;

        throw new VglError(VGL_ERROR_CODE.BROKER_SOFTFAIR_TRANSMIT_FAILED, {
          additionalData: `BrokerId: ${brokerId} / pool: ${pool} / insuranceId: ${insuranceId}`,
          cause: error,
        });
      }
    },

    async loadRights() {
      try {
        const api = useApi().broker;

        this.rights.isLoading = true;

        const [, request] = api.loadRights();

        const result = await request;
        if (result === null) {
          throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
            additionalErrorMessage: 'broker -> loadRights',
          });
        }

        this.rights.data = result;
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.BROKER_LOADING_RIGHTS_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
          cause: error,
        });
      } finally {
        this.rights.isLoading = false;
      }
    },

    startHeartbeat() {
      if (this.heartbeatStarted) return;

      this.heartbeatStarted = true;
      useIntervalFn(
        () => {
          try {
            const api = useApi().broker;
            const [, request] = api.heartbeat(useTariffStore().calculationId);
            request.catch(() => {
              // ignore possible errors
            });
          } catch (_) {
            // ignore possible errors
          }
        },
        1000 * 60 * 30,
      ); // execute every 30 minutes
    },

    async loadSettings() {
      try {
        const { defaultUserSettings } = getCurrentSpartenConfig();
        const brokerId = this.id;
        const api = useApi().broker;

        this.settings.isLoading = true;
        const [, request] = api.loadSettings(brokerId);

        this.settings.data = {
          ...this.settings.data,
          ...defaultUserSettings,
        };

        const response = await request;

        if (response !== null) {
          this.settings.data = {
            ...this.settings.data,
            ...response,
          };
        }
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.BROKER_LOADING_SETTINGS_FAILED, {
          cause: error,
        });
      } finally {
        this.settings.isLoading = false;
      }
    },

    async saveBrokerSettings() {
      try {
        const api = useApi().broker;
        const brokerId = this.id;
        const settings = this.settings.data;

        this.settings.isLoading = true;

        const [, request] = api.saveBrokerSettings(brokerId, settings);
        await request;
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.BROKER_LOADING_SETTINGS_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
          cause: error,
        });
      } finally {
        this.settings.isLoading = false;
      }
    },

    resetBedarfInformation() {
      this.bedarfInformation.bedarfId = undefined;
      this.bedarfInformation.clientId = undefined;
    },
  },

  getters: {
    isAdmin(state) {
      return state.rights.data?.isAdmin ?? false;
    },

    canDownLoadLogs(state) {
      return state.rights.data?.tarife.logDownload ?? false;
    },

    showProfileCreationWarning(state) {
      return state.rights.data?.profile.showCreationWarning ?? false;
    },

    id(state) {
      if (state.broker.data === undefined) {
        throw new VglError(VGL_ERROR_CODE.BROKER_ID_NOT_SET, {
          additionalErrorMessage: JSON.stringify(state.broker),
        });
      }

      return state.broker.data.id;
    },

    canCreateInitialApplication(state) {
      return state.rights.data?.antrag.erstantrag ?? false;
    },
  },
});
